import '.d{display:flex;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
/* eslint-disable react-refresh/only-export-components */
import { useContext, useState, useCallback, type ReactNode } from 'react'
import { styled, type Style } from 'inlines'
import { Text } from '../typography/Text'
import useMediaQuery from '@app/util/useMediaQuery'
import { LinkWrapper } from '../link/LinkWrapper'
import { Image } from '../image/Image'
import { TabRow } from './internal/TabRow'
import { TabsContext } from './internal/TabsContext'

export type HeaderTabsGroupProps<T extends string> = {
  selectedTab: T
  style?: Style
  children: ReactNode
}

export type HeaderTabProps<T extends string> = {
  value: T
  label: string
  icon: string
  activeIcon?: string
  href: string
  onSelectTab: (key: T) => void
}

const Group = <T extends string>({
  selectedTab,
  style,
  children,
}: HeaderTabsGroupProps<T>) => {
  return (
    <TabRow selectedTab={selectedTab} style={style}>
      {children}
    </TabRow>
  )
}

const Tab = <T extends string>({
  value,
  label,
  icon,
  activeIcon,
  href,
  onSelectTab,
}: HeaderTabProps<T>) => {
  const context = useContext(TabsContext)
  const isMobile = useMediaQuery()

  if (!context) {
    throw new Error('HeaderTabs.Tab must be used within HeaderTabs.Group')
  }

  if (!label && !icon) {
    throw new Error('Tab should either have label or icon')
  }

  const { selectedTab } = context
  const isSelected = selectedTab === value
  const [isHovered, setIsHovered] = useState(false)

  const tabStyle: Style = {
    padding: isMobile ? '1rem 0.5rem' : '1.5rem 1rem',
    justifyContent: 'center',
    alignItems: 'center',
    gap: isMobile ? '1rem' : '2.5rem',
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--brand-secondary)',
    },
    borderBottom: `3px ${isSelected ? 'var(--brand-secondary)' : 'transparent'
      } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    color: isSelected || isHovered ? 'var(--brand-secondary)' : 'var(--gray-300)',
    fontFamily: 'FunnelDisplay',
    fontSize: isMobile ? '0.75rem' : '1.75rem',
  }

  const onTabSelect = useCallback(() => {
    onSelectTab(value)
  }, [value, onSelectTab])

  const showActiveIcon = (isSelected || isHovered) && activeIcon
  const displayIcon = showActiveIcon ? activeIcon : icon

  const renderedIcon = displayIcon ? (
    <Image
      src={displayIcon}
      style={{
        width: isMobile ? '1.875rem' : '2.5rem',
        height: isMobile ? '1.875rem' : '2.5rem',
        objectFit: 'contain',
      }}
    />
  ) : null

  return (
    <LinkWrapper to={href}>
      <styled.div
        data-state={isSelected ? 'Selected' : 'Default'}
        style={tabStyle}
        onClick={onTabSelect}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="d r"
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '0.5rem' : '1rem',
          }}
        >
          {renderedIcon}
          <Text.Medium style={textStyle}>{label}</Text.Medium>
        </div>
      </styled.div>
    </LinkWrapper>
  )
}

export const HeaderTabs = {
  Group,
  Tab,
}
